import { useCallback, useEffect, useRef, useState } from 'react'

/** How long a playing video's controls stay up after the last activity. */
export const CONTROLS_IDLE_MS = 3000

export interface ControlsAutoHideOptions {
  /** The `.card-media-video` surface: activity anywhere on it reveals the controls. */
  element: HTMLElement | null
  /** Off for decorative videos: always shown, no listeners or timers. */
  enabled: boolean
  playing: boolean
  /**
   * The owner's pins (speed menu open, scrub in flight). Keyboard focus inside
   * the surface pins too, detected here.
   */
  locked: boolean
  /**
   * A touch/pen tap on hidden controls only reveals them. Off where a tap on
   * the video is navigation (`vertical_stories`).
   */
  tapReveals: boolean
  idleMs?: number
}

export interface ControlsAutoHide {
  shown: boolean
  /** Read-and-clear: true when the click in progress ends a reveal-only tap. */
  consumeRevealTap: () => boolean
}

/**
 * `:focus-visible` is how a Tab focus is told from a mouse click; where the
 * selector is unsupported, any focus counts.
 */
function isFocusVisible(node: Element): boolean {
  try {
    return node.matches(':focus-visible')
  } catch {
    return true
  }
}

/**
 * Keyboard focus on something inside `el`. The widget renders inside a shadow
 * root, where `document.activeElement` is only the host, so the element's own
 * root is asked.
 */
function keyboardFocusWithin(el: HTMLElement | null): boolean {
  if (!el) return false
  const root = el.getRootNode() as Document | ShadowRoot
  const active = root.activeElement
  return !!active && el.contains(active) && isFocusVisible(active)
}

/**
 * Auto-hides a playing video's controls, YouTube-style.
 *
 * `shown = !enabled || !playing || locked || visible`. `visible` goes true on
 * any activity (pointer move/down, key, focus) and on every play start,
 * including visibility autoplay, and an idle timer of `idleMs` turns it false
 * again unless the video stopped, a pin is set or keyboard focus is inside.
 * A mouse leaving the surface hides at once; touch and pen leaves are ignored.
 * Pausing, pinning or disabling shows the controls immediately and stops the
 * timer; a pin lifting while playing re-arms it.
 *
 * The owner hides with opacity and pointer-events, never `visibility`, so the
 * buttons stay in the tab order and the accessibility tree: tabbing into one
 * reveals the controls.
 *
 * Studio previews auto-hide too; FeedCard.css pins the controls visible under
 * `[data-inspect-mode='true']` (Studio's inspect frame) so the inspect regions
 * stay hoverable.
 */
export function useControlsAutoHide({
  element,
  enabled,
  playing,
  locked,
  tapReveals,
  idleMs = CONTROLS_IDLE_MS,
}: ControlsAutoHideOptions): ControlsAutoHide {
  const [visible, setVisible] = useState(true)
  const visibleRef = useRef(true)
  const enabledRef = useRef(enabled)
  const playingRef = useRef(playing)
  const lockedRef = useRef(locked)
  const tapRevealsRef = useRef(tapReveals)
  const elementRef = useRef(element)
  const idleMsRef = useRef(idleMs)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const revealTapRef = useRef(false)

  // Read by the timer and the native listeners, which outlive a render.
  enabledRef.current = enabled
  playingRef.current = playing
  lockedRef.current = locked
  tapRevealsRef.current = tapReveals
  elementRef.current = element
  idleMsRef.current = idleMs

  const clear = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  // Called on every pointermove: only a hidden -> shown change re-renders.
  const show = useCallback(() => {
    if (visibleRef.current) return
    visibleRef.current = true
    setVisible(true)
  }, [])

  const hideNow = useCallback(() => {
    clear()
    visibleRef.current = false
    setVisible(false)
  }, [clear])

  const arm = useCallback(() => {
    clear()
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      if (!enabledRef.current || !playingRef.current || lockedRef.current) return
      if (keyboardFocusWithin(elementRef.current)) return
      visibleRef.current = false
      setVisible(false)
    }, idleMsRef.current)
  }, [clear])

  const reveal = useCallback(() => {
    show()
    if (enabledRef.current && playingRef.current && !lockedRef.current) arm()
  }, [arm, show])

  // Play start (click or autoplay) reveals and arms; pause or disable shows.
  useEffect(() => {
    if (!enabled || !playing) {
      clear()
      show()
      return
    }
    reveal()
    return clear
  }, [clear, enabled, playing, reveal, show])

  // A pin shows and stops the timer; lifting it while playing re-arms.
  useEffect(() => {
    if (locked) {
      clear()
      show()
    } else if (enabledRef.current && playingRef.current) {
      arm()
    }
  }, [arm, clear, locked, show])

  useEffect(() => {
    if (!element || !enabled) return
    const onActivity = () => { reveal() }
    const onPointerDown = (e: PointerEvent) => {
      // Decided before `reveal()` flips `visible`. Reset on every press.
      revealTapRef.current = enabledRef.current
        && playingRef.current
        && !visibleRef.current
        && tapRevealsRef.current
        && e.pointerType !== 'mouse'
      reveal()
    }
    const onPointerLeave = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      if (!playingRef.current || lockedRef.current || keyboardFocusWithin(element)) return
      hideNow()
    }
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget
      if (next instanceof Node && element.contains(next)) return
      if (playingRef.current && !lockedRef.current) arm()
    }
    const passive = { passive: true }
    element.addEventListener('pointermove', onActivity, passive)
    element.addEventListener('pointerdown', onPointerDown, passive)
    element.addEventListener('pointerleave', onPointerLeave, passive)
    element.addEventListener('keydown', onActivity)
    element.addEventListener('focusin', onActivity)
    element.addEventListener('focusout', onFocusOut)
    return () => {
      element.removeEventListener('pointermove', onActivity)
      element.removeEventListener('pointerdown', onPointerDown)
      element.removeEventListener('pointerleave', onPointerLeave)
      element.removeEventListener('keydown', onActivity)
      element.removeEventListener('focusin', onActivity)
      element.removeEventListener('focusout', onFocusOut)
    }
  }, [arm, element, enabled, hideNow, reveal])

  useEffect(() => clear, [clear])

  const consumeRevealTap = useCallback(() => {
    const was = revealTapRef.current
    revealTapRef.current = false
    return was
  }, [])

  return { shown: !enabled || !playing || locked || visible, consumeRevealTap }
}
